import convertDecimalWithComma from "@/app/infrastructures/misc/common-library/ConvertDecimalWithComma";
import {
  HandoverFailedSttItem,
  HandoverFailedVendorSttItem,
  FAILED_VENDOR_STT_STATUS_SUCCESS,
  FAILED_VENDOR_STT_STATUS_FAILED
} from "@/domain/entities/Handover";
import {
  VendorOrderStatusEnum,
  getVendorOrderStatusBadgeStyle,
  getVendorOrderStatusLabel,
  resolveVendorOrderStatusEnum,
  INTERIM_VENDOR_ORDER_STATUS_LABELS
} from "../modules/vendorOrderStatusLabel";
import {
  HandoverStatusEnum,
  getHandoverStatusBadgeStyle,
  getHandoverStatusLabel,
  resolveHandoverStatusEnum,
  DEFAULT_HANDOVER_STATUS_LABELS
} from "../modules/handoverStatusLabel";

export {
  VendorOrderStatusEnum,
  getVendorOrderStatusBadgeStyle,
  getVendorOrderStatusLabel,
  resolveVendorOrderStatusEnum,
  HandoverStatusEnum,
  getHandoverStatusBadgeStyle,
  getHandoverStatusLabel,
  resolveHandoverStatusEnum,
  DEFAULT_HANDOVER_STATUS_LABELS
};

export const VENDOR_ORDER_STATUS_NON_API = VendorOrderStatusEnum.NON_API;
export const VENDOR_ORDER_STATUS_SUCCESS = VendorOrderStatusEnum.SUCCESS;
export const VENDOR_ORDER_STATUS_PARTIAL_SUCCESS =
  VendorOrderStatusEnum.PARTIAL_SUCCESS;
export const VENDOR_ORDER_STATUS_FAILED = VendorOrderStatusEnum.FAILED;
export const VENDOR_ORDER_STATUS_IN_PROGRESS =
  VendorOrderStatusEnum.IN_PROGRESS;
export const VENDOR_ORDER_STATUS_UNPROCESSED =
  VendorOrderStatusEnum.UNPROCESSED;

export const DEFAULT_API_STATUS_LABELS = {
  success: INTERIM_VENDOR_ORDER_STATUS_LABELS[VendorOrderStatusEnum.SUCCESS],
  failed: INTERIM_VENDOR_ORDER_STATUS_LABELS[VendorOrderStatusEnum.FAILED],
  reason: "Alasan",
  viewReason: "Lihat alasan"
};

export type HandoverStatusBadgeLabels =
  | Partial<Record<HandoverStatusEnum, string>>
  | {
      success?: string;
      partialSuccess?: string;
      failed?: string;
    };

function escapeHtml(value: string): string {
  return value
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

function badgePill(label: string, bg: string, text: string, icon = ""): string {
  return `<div class="inline-flex items-center rounded-full px-2 py-0.5 font-medium ${bg} ${text}">
      ${icon ? `<img src="${icon}" class="w-4 mr-1" alt="" />` : ""}
      <span>${label}</span>
    </div>`;
}

function lihatAlasanTooltip(
  message: string,
  reasonLabel: string,
  viewReasonLabel: string
): string {
  const safeMessage = escapeHtml(message || "-");
  return `<div class="relative inline-block group cursor-pointer mt-1">
      <span class="text-black-lp-300 underline text-12px font-semibold">${escapeHtml(
        viewReasonLabel
      )}</span>
      <div style="width:354px;background:#4D4D4D" class="pointer-events-none opacity-0 group-hover:opacity-100 transition-opacity duration-150 absolute z-50 top-full right-0 mt-2 rounded-xl p-3 text-left shadow-lg">
        <div class="font-bold text-white text-12px">${escapeHtml(
          reasonLabel
        )}</div>
        <div class="text-white text-12px font-normal mt-1 break-words max-h-48 overflow-y-auto">${safeMessage}</div>
        <div class="absolute top-0 right-6 transform -translate-y-1/2 w-2 h-2 bg-black-lp-300 rotate-45"></div>
      </div>
    </div>`;
}

export function isApiResponseStatusFailed(status: string): boolean {
  const upper = (status || "").trim().toUpperCase();
  return (
    upper === VendorOrderStatusEnum.FAILED ||
    status === FAILED_VENDOR_STT_STATUS_FAILED
  );
}

export function renderApiResponseBadgeHtml(
  status: string,
  labels = DEFAULT_API_STATUS_LABELS
): string {
  const upper = (status || "").trim().toUpperCase();
  if (
    upper === VendorOrderStatusEnum.SUCCESS ||
    status === FAILED_VENDOR_STT_STATUS_SUCCESS
  ) {
    const badge = getVendorOrderStatusBadgeStyle(VendorOrderStatusEnum.SUCCESS);
    return badgePill(labels.success, badge.bg, badge.text, badge.icon);
  }
  if (
    upper === VendorOrderStatusEnum.FAILED ||
    status === FAILED_VENDOR_STT_STATUS_FAILED
  ) {
    const badge = getVendorOrderStatusBadgeStyle(VendorOrderStatusEnum.FAILED);
    return badgePill(labels.failed, badge.bg, badge.text, badge.icon);
  }
  return `<span class="text-black-lp-300">-</span>`;
}

export function buildApiResponseTooltipHtml(
  message: string,
  reasonLabel = DEFAULT_API_STATUS_LABELS.reason
): string {
  const safeMessage = escapeHtml(message || "-");
  return `<div style="text-align:left!important;" class="text-left font-montserrat"><div class="font-bold text-white text-12px mb-1" style="font-weight:700;">${escapeHtml(
    reasonLabel
  )}</div><div class="text-white text-12px font-normal break-words max-h-48 overflow-y-auto" style="font-weight:400;word-break:break-word;">${safeMessage}</div></div>`;
}

// renderApiResponseStatusCell renders the "Status Respon API" cell for one
// successful-handover STT row: a colored badge plus, on failure, a
// hover-only dark tooltip showing the vendor's reason (Figma 13084:395274).
export function renderApiResponseStatusCell(
  status: string,
  message: string,
  labels = DEFAULT_API_STATUS_LABELS
): string {
  if (isApiResponseStatusFailed(status)) {
    const badgeHtml = renderApiResponseBadgeHtml(status, labels);
    return `<div class="flex flex-col items-start">${badgeHtml}${lihatAlasanTooltip(
      message,
      labels.reason,
      labels.viewReason
    )}</div>`;
  }
  return renderApiResponseBadgeHtml(status, labels);
}

// resolveFailedSttReason mirrors the existing account-locale convention used
// elsewhere in HandoverMapper (AccountController.accountData.accountIsForeign)
// and falls back to the em dash per GENESIS-24792 when both reason fields are empty.
export function resolveFailedSttReason(
  item: HandoverFailedSttItem,
  isForeignAccount: boolean
): string {
  const reason = isForeignAccount
    ? item.reasonEn || item.reason
    : item.reason || item.reasonEn;
  return reason && reason.trim() ? reason : "—";
}

const DEFAULT_FAILED_STT_COLUMN_LABELS: Record<string, string> = {
  "handover.list.detail.sttNo": "No. STT/Shipment",
  "handover.list.detail.product": "Produk",
  "handover.list.detail.commodity": "Komoditas",
  "handover.list.detail.pieces": "Koli",
  "handover.list.detail.grossWeight": "Berat Kotor",
  "handover.list.detail.volumeWeight": "Berat Dimensi",
  "handover.list.detail.reason": "Alasan"
};

export function buildFailedSttColumns(
  isForeignAccount: boolean,
  translate: (key: string) => string = (key) =>
    DEFAULT_FAILED_STT_COLUMN_LABELS[key] || key
): Array<{
  name: string;
  styleHead: string;
  styleCustom?: string;
  render: (...args: any[]) => string;
}> {
  return [
    {
      name: "No.",
      styleHead: "w-10 text-left whitespace-nowrap",
      render: (_: any, i: number) =>
        `<span class="font-normal text-black-lp-300 whitespace-nowrap">${i +
          1}</span>`
    },
    {
      name: translate("handover.list.detail.sttNo"),
      styleHead: "w-56 text-left whitespace-nowrap",
      render: (item: HandoverFailedSttItem) =>
        `<div class="text-black-lp-300 whitespace-nowrap">${item.sttNo ||
          item.shipmentId ||
          "-"}</div>`
    },
    {
      name: translate("handover.list.detail.product"),
      styleHead: "w-40 text-left whitespace-nowrap",
      render: (item: HandoverFailedSttItem) =>
        `<div class="text-black-lp-300 flex">
          <div class="rounded px-2 py-0 bg-gray-lp-400">${item.productType ||
            "-"}</div>
        </div>`
    },
    {
      name: translate("handover.list.detail.commodity"),
      styleHead: "w-72 text-left whitespace-nowrap",
      styleCustom: "w-72 text-left",
      render: (item: HandoverFailedSttItem) =>
        `<div class="font-normal text-black-lp-300 break-words pr-2">${item.commodityName ||
          "-"}</div>`
    },
    {
      name: translate("handover.list.detail.pieces"),
      styleHead: "w-24 text-left whitespace-nowrap",
      styleCustom: "justify-center",
      render: (item: HandoverFailedSttItem) =>
        `<span class="font-normal text-black-lp-300 whitespace-nowrap">${item.totalPiece}</span>`
    },
    {
      name: translate("handover.list.detail.grossWeight"),
      styleHead: "w-40 text-left whitespace-nowrap",
      render: (item: HandoverFailedSttItem) =>
        `<span class="font-normal text-black-lp-300 whitespace-nowrap">${convertDecimalWithComma(
          item.grossWeight,
          2
        )} Kg</span>`
    },
    {
      name: translate("handover.list.detail.volumeWeight"),
      styleHead: "w-40 text-left whitespace-nowrap",
      render: (item: HandoverFailedSttItem) =>
        `<span class="font-normal text-black-lp-300 whitespace-nowrap">${convertDecimalWithComma(
          item.volumeWeight,
          2
        )} Kg</span>`
    },
    {
      name: translate("handover.list.detail.reason"),
      styleHead: "w-72 text-left",
      render: (item: HandoverFailedSttItem) =>
        `<span class="font-normal text-black-lp-300">${escapeHtml(
          resolveFailedSttReason(item, isForeignAccount)
        )}</span>`
    }
  ];
}

// resolveHandoverStatusBadgeHtml renders the sidebar "Status Handover" badge
// (Figma 13084:391421). Non-API vendors render no badge at all (as-is design).
export function resolveHandoverStatusBadgeHtml(
  vendorOrderStatus: string,
  labels?: HandoverStatusBadgeLabels
): string {
  let resolved = resolveHandoverStatusEnum(vendorOrderStatus);
  if (!resolved && vendorOrderStatus) {
    const upper = vendorOrderStatus.trim().toUpperCase();
    if (upper === "BERHASIL HIT API") {
      resolved = HandoverStatusEnum.SUCCESS;
    } else if (upper === "SEBAGIAN HIT API BERHASIL") {
      resolved = HandoverStatusEnum.PARTIAL_SUCCESS;
    } else if (upper === "GAGAL HIT API") {
      resolved = HandoverStatusEnum.FAILED;
    }
  }

  if (!resolved) {
    return "";
  }

  const badge = getHandoverStatusBadgeStyle(resolved);
  let label = DEFAULT_HANDOVER_STATUS_LABELS[resolved] || "";

  if (labels) {
    if (resolved === HandoverStatusEnum.SUCCESS) {
      label =
        (labels as any).success ||
        (labels as any)[HandoverStatusEnum.SUCCESS] ||
        label;
    } else if (resolved === HandoverStatusEnum.PARTIAL_SUCCESS) {
      label =
        (labels as any).partialSuccess ||
        (labels as any)[HandoverStatusEnum.PARTIAL_SUCCESS] ||
        label;
    } else if (resolved === HandoverStatusEnum.FAILED) {
      label =
        (labels as any).failed ||
        (labels as any)[HandoverStatusEnum.FAILED] ||
        label;
    }
  }

  return badgePill(label, badge.bg, badge.text, badge.icon);
}

// countFailedRespondedStt returns how many STTs in the vendor API call list
// are "Gagal Respon" — used both to size the retry modal's counter text and
// to gate the "Ulangi proses" button's visibility.
export function countFailedRespondedStt(
  stt: HandoverFailedVendorSttItem[]
): number {
  return stt.filter((item) => {
    const upper = (item.status || "").trim().toUpperCase();
    return (
      upper === VendorOrderStatusEnum.FAILED ||
      item.status === FAILED_VENDOR_STT_STATUS_FAILED
    );
  }).length;
}
